/**
 * StudentCorrectionsSection — Fase 2 · sección superior de la pestaña
 * «Correcciones» del alumno.
 *
 * Lista únicamente las actividades de Feedback Hub publicadas al alumno
 * (`GET /api/activities/my-corrections`). Se coloca ARRIBA del contenido
 * legacy (tipo `correction`) sin modificarlo. Al pulsar en cada tarjeta el
 * alumno abre el mismo PDF con los overlays de FH, sus respuestas
 * originales precargadas y los campos editables — el visor se activa en
 * `correctionMode` y el botón principal cambia a «Enviar corrección».
 *
 * Reglas invariantes (ver PRD.md, Iteración 42):
 *  - Solo el alumno ve esta sección.
 *  - «Enviar corrección» POSTea a `/manual-correction`, que NO llama a
 *    Feedback Hub ni dispara un segundo auto-correct.
 *  - Estado del pill del alumno: «Pendiente de corregir» → «Corrección
 *    enviada» — refleja el mismo dato que el docente ve en «Corrección
 *    recibida».
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Eye } from 'lucide-react';
import PdfActivityDialog from '@/components/PdfActivityDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudentCorrectionsSection({ classId, token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openItem, setOpenItem] = useState(null); // full row

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/activities/my-corrections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const all = res.data?.corrections || [];
      // Only show corrections for the current class so the section fits
      // naturally inside the class-scoped "Correcciones" tab.
      const scoped = classId ? all.filter(c => c.class_id === classId) : all;
      setItems(scoped);
    } catch {
      setItems([]);
    }
    setLoading(false);
  }, [token, classId]);

  useEffect(() => { load(); }, [load]);

  if (!loading && items.length === 0) return null;

  return (
    <div data-testid="student-corrections-section" className="mb-6">
      <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
        Correcciones del tutor
      </h4>
      {loading ? (
        <p className="text-xs text-slate-400">Cargando…</p>
      ) : (
        <div className="space-y-2" data-testid="student-corrections-list">
          {items.map(c => {
            const sent = c.manual_correction_state === 'received';
            return (
              <div
                key={c.activity_id}
                data-testid={`student-correction-${c.activity_id}`}
                data-state={sent ? 'sent' : 'pending'}
                className="rounded-lg p-3 bg-emerald-50 border border-emerald-100 flex items-center justify-between gap-2"
              >
                <div className="flex items-center gap-2 text-sm min-w-0">
                  <FileText className="w-4 h-4 text-emerald-700 shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium truncate">{c.content_title || 'Actividad'}</p>
                    <p className="text-[11px] text-slate-500">
                      Publicada · {c.published_at ? new Date(c.published_at).toLocaleString() : ''}
                    </p>
                  </div>
                  <span
                    data-testid={`student-correction-state-${c.activity_id}`}
                    className={`text-[10px] px-2 py-0.5 rounded-full border ${sent
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-800'
                      : 'bg-amber-50 border-amber-200 text-amber-800'}`}
                  >
                    {sent ? 'Corrección enviada' : 'Pendiente de corregir'}
                  </span>
                </div>
                {/* Fase 2 · Once the student has sent the manual correction
                    (`manual_correction_state === 'received'`, persisted in
                    db.activities via /manual-correction) the card is locked
                    read-only: no re-open, no re-edit, no re-submit. The
                    pill above already communicates the final state
                    («Corrección enviada»). Lock survives reload/logout/
                    device swap because it derives from the server state
                    returned by /my-corrections. */}
                {!sent && (
                  <button
                    type="button"
                    data-testid={`student-correction-open-${c.activity_id}`}
                    onClick={() => setOpenItem(c)}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <Eye className="w-3 h-3" /> Abrir corrección
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {openItem && (
        <PdfActivityDialog
          open={true}
          onClose={() => setOpenItem(null)}
          contentId={openItem.content_id}
          fileName={openItem.content_title || 'Corrección'}
          token={token}
          reviewActivityId={openItem.activity_id}
          correctionMode={true}
          onCorrectionSent={load}
        />
      )}
    </div>
  );
}
